import type * as am4charts from '@amcharts/amcharts4/charts';
import * as am4core from '@amcharts/amcharts4/core';
import type { Theme } from '@mui/material/styles';

const DEFAULT_FONT_SIZE = 12;

export const getPreviewColors = (theme: Theme): am4core.Color[] => {
  const palette = theme.palette.data.sequence;
  const safePalette = palette?.length ? palette : [theme.palette.primary.main];
  return safePalette.map((color) => am4core.color(color));
};

export const applyCategoryAxisStyle = (
  axis: am4charts.CategoryAxis,
  theme: Theme,
  options?: {
    inverted?: boolean;
    fontSize?: number;
    maxWidth?: number;
    minGridDistance?: number;
    showGrid?: boolean;
    showLine?: boolean;
  },
): void => {
  const { inverted, fontSize = DEFAULT_FONT_SIZE, maxWidth, minGridDistance = 20, showGrid = false } = options ?? {};
  const showLine = options?.showLine ?? true;

  axis.renderer.minGridDistance = minGridDistance;
  axis.renderer.inversed = Boolean(inverted);
  axis.renderer.labels.template.fill = am4core.color(theme.palette.text.secondary);
  axis.renderer.labels.template.fontSize = fontSize;
  axis.renderer.labels.template.truncate = true;
  if (maxWidth) {
    axis.renderer.labels.template.maxWidth = maxWidth;
  }
  axis.renderer.ticks.template.disabled = true;
  axis.renderer.grid.template.disabled = !showGrid;
  axis.renderer.line.strokeOpacity = showLine ? 1 : 0;
};

export const applyValueAxisStyle = (
  axis: am4charts.ValueAxis,
  theme: Theme,
  options?: {
    fontSize?: number;
    min?: number;
    showGrid?: boolean;
    showLine?: boolean;
  },
): void => {
  const { fontSize = DEFAULT_FONT_SIZE, min, showGrid = false, showLine = false } = options ?? {};

  if (typeof min === 'number') {
    axis.min = min;
  }
  axis.renderer.labels.template.fill = am4core.color(theme.palette.text.secondary);
  axis.renderer.labels.template.fontSize = fontSize;
  axis.renderer.ticks.template.disabled = true;
  axis.renderer.grid.template.disabled = !showGrid;
  axis.renderer.grid.template.stroke = am4core.color(theme.palette.chart.grid);
  axis.renderer.grid.template.strokeOpacity = showGrid ? 1 : 0;
  axis.renderer.line.strokeOpacity = showLine ? 1 : 0;
};

export const applyPreviewTooltipStyle = (
  tooltip: am4core.Tooltip,
  theme: Theme,
  options?: {
    background?: 'paper' | 'default';
    opacity?: number;
    strokeWidth?: number;
    textColor?: string;
  },
): void => {
  const {
    background = 'paper',
    opacity = 0.9,
    strokeWidth = 0,
    textColor = theme.palette.text.primary,
  } = options ?? {};

  tooltip.background.strokeWidth = strokeWidth;
  tooltip.background.cornerRadius = theme.shape.borderRadius;
  tooltip.background.pointerLength = 0;
  tooltip.background.valign = 'middle';
  tooltip.getFillFromObject = false;
  tooltip.background.fill = am4core.color(
    background === 'default' ? theme.palette.background.default : theme.palette.background.paper,
  );
  tooltip.background.fillOpacity = opacity;
  tooltip.label.fill = am4core.color(textColor);
  tooltip.label.padding(0, 0, 0, 0);
  tooltip.zIndex = Infinity;
};

export const buildXYTooltipHTML = (
  theme: Theme,
  params: {
    categoryKey: 'categoryX' | 'categoryY';
    valueKey: 'valueX' | 'valueY';
    textColor?: string;
  },
): string => {
  const { categoryKey, valueKey, textColor = theme.palette.text.primary } = params;

  return `<div style="min-width: 170px;display: grid;grid-column-gap: 10px;color: ${textColor};border-radius: ${
    theme.shape.borderRadius
  }px;padding: ${theme.shape.padding[2]}px; background-color:${theme.palette.background.paper};">
    <div style="background: {fill};width: 12px;height: 12px;border-radius: 50%;margin-top: 6px;grid-row-start: 1;grid-column-start: 1;" ></div>
    <p style="margin: 0px;padding: 0px;grid-column-start: 2;grid-row-start: 1;" >{${categoryKey}}</p>
    <p style="margin: 0px;padding: 0px;grid-column-start: 2;grid-row-start: 2;" >{${valueKey}.formatNumber('$#,###.00')}</p>
  </div>`;
};

export const buildPieTooltipHTML = (theme: Theme): string =>
  `<div style="display: grid;grid-column-gap: 10px;color: ${theme.palette.text.secondary};border-radius: ${
    theme.shape.borderRadius
  }px;padding: ${theme.shape.padding[2]}px; background-color:${theme.palette.background.paper}">
    <div style="background: {fill};width: 12px;height: 12px;border-radius: 50%;margin-top: 6px;grid-row-start: 1;grid-column-start: 1;" ></div>
    <p style="margin: 0px;padding: 0px;grid-column-start: 2;grid-row-start: 1;" >{category}</p>
    <p style="margin: 0px;padding: 0px;grid-column-start: 2;grid-row-start: 2;" >{value.formatNumber('$#,###.00')}</p>
  </div>`;

export const applyColumnCornerRadius = (
  template: am4core.RoundedRectangle,
  options: { direction: 'vertical' | 'horizontal'; radius?: number; valueKey: 'valueX' | 'valueY' },
): void => {
  const { direction, radius = 4, valueKey } = options;

  const getValue = (target: am4core.Sprite): number | null => {
    const dataItem = target.dataItem as am4charts.ColumnSeriesDataItem | undefined;
    const value = dataItem?.values?.[valueKey]?.value;
    return typeof value === 'number' ? value : null;
  };

  const cornerRadius = (sign: number) => (currentRadius: number, target: am4core.Sprite) => {
    const value = getValue(target);
    if (value === null) {
      return currentRadius;
    }
    const isMatch = Math.sign(value) === sign;
    if (!isMatch) {
      return currentRadius;
    }

    return radius;
  };

  if (direction === 'vertical') {
    template.adapter.add('cornerRadiusTopLeft', cornerRadius(1));
    template.adapter.add('cornerRadiusTopRight', cornerRadius(1));
    template.adapter.add('cornerRadiusBottomLeft', cornerRadius(-1));
    template.adapter.add('cornerRadiusBottomRight', cornerRadius(-1));
    return;
  }

  template.adapter.add('cornerRadiusTopLeft', cornerRadius(-1));
  template.adapter.add('cornerRadiusBottomLeft', cornerRadius(-1));
  template.adapter.add('cornerRadiusTopRight', cornerRadius(1));
  template.adapter.add('cornerRadiusBottomRight', cornerRadius(1));
};
